import {useEffect, useState, createContext, useContext} from 'react';
import {useZone} from 'use-zone-breakpoints';

/**
 * @typedef {Readonly<{
 *   mobile: boolean,
 *   mobileAndUp: boolean,
 *   mobileAndDown: boolean,
 *   desktop: boolean,
 *   desktopAndUp: boolean,
 *   desktopAndDown: boolean,
 * }>} BreakpointState
 */

const BP_MOBILE = Object.freeze({
	mobile: true,
	mobileAndUp: true,
	mobileAndDown: true,

	desktop: false,
	desktopAndUp: false,
	desktopAndDown: true,
});

const BP_DESKTOP = Object.freeze({
	mobile: false,
	mobileAndUp: true,
	mobileAndDown: false,

	desktop: true,
	desktopAndUp: true,
	desktopAndDown: true,
});

/**
 * returns `null` on first render/SSR
 *
 * @returns {BreakpointState | null} ...
 */
export function useBreakpoint() {
	const z = useZone();
	if (z === -1) {
		return null;
	} else if (z === 0) {
		return BP_MOBILE;
	} else {
		return BP_DESKTOP;
	}
}

/**
 * `[min, max)`, Returns `null` on first render/ssr.
 *
 * @param {number} [min]
 * @param {number} [max]
 * @returns {number | null} ...
 */
export function useRandInt(min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER) {
	const rand_ = useState(null);
	/** @type {[number | null, import("react").Dispatch<import("react").SetStateAction<number | null>>]} */
	const [rand, setRand] = rand_;

	useEffect(
		() => {
			const min2 = Math.ceil(min);
			const max2 = Math.floor(max);
			const rand2 = Math.floor(Math.random() * (max2 - min2)) + min2;
			setRand(rand2);
		},
		[min, max, setRand],
	);

	return rand;
}

/**
 * Returns the location hash without pre-pended "#", or `null`. Adds an event listener, do not
 * over-use.
 *
 * @returns {string | null} ...
 */
export function useLocationHash() {
	/** @type {[string | null, import("react").Dispatch<import("react").SetStateAction<string | null>>]} */
	const hash_ = useState(null);
	const [hash, setHash] = hash_;

	useEffect(
		() => {
			function onHashChange() {
				const newHash = (() => {
					const hash2 = window.location.hash;
					// eslint-disable-next-line yoda
					if (typeof hash2 === 'string' && 1 < hash2.length) {
						return hash2.substring(1);
					}
					return null;
				})();
				setHash(newHash);
			}
			onHashChange();
			window.addEventListener('hashchange', onHashChange);
			return () => {
				window.removeEventListener('hashchange', onHashChange);
			};
		},
		[setHash],
	);

	return hash;
}

/**
 * @type {import("react").Context<
 *   null | import("gatsby").PageProps<
 *     undefined,
 *     {
 *       meta?: import("./page-metadata").PageMetadata,
 *     }
 *   >
 * >}
 */
export const PagePropsContext = createContext(null);

/**
 * @returns {import("gatsby").PageProps<
 *   undefined,
 *   {
 *     meta?: import("./page-metadata").PageMetadata,
 *   }
 * >} ...
 */
export function usePageProps() {
	return useContext(PagePropsContext);
}
